/**
 * A Bruno collection FOLDER (already read into memory) → the same result
 * shape `parseImportFile` returns, so the Import dialog's preview and Import
 * button do not care whether a file or a folder came in.
 *
 * Pure: no DOM, no Tauri, no network — reading is `readFolderFiles.js`'s job.
 *
 * Layouts (both may appear in one collection; Bruno allows it):
 *   bruno.json + collection.bru + folder.bru + *.bru + environments/*.bru      (≤ 3.0)
 *   opencollection.yml + folder.yml + *.yml + environments/*.yml               (3.1+)
 * The collection root is the directory holding `bruno.json` or
 * `opencollection.yml`. If the picked folder has neither, each immediate
 * child directory that has one becomes its own collection.
 */
import { loadImportYaml } from '../safeYaml';
import { bruRequestToNode, bruDefaultsFromText, bruEnvFromText } from '../bruno/fromBru';
import { ymlItemToNode, ymlFolderMeta, ymlDefaults, ymlEnvToEnv } from '../bruno/fromYaml';
import { emptyDefaults } from '../bruno/ir';
import { irToNormalized, secretEnvWarning } from '../bruno/toNormalized';
import { formatLabel } from '../detectFormat';

export const NOT_A_BRUNO_FOLDER =
  'This folder is not a Bruno collection (no bruno.json or opencollection.yml).';

const MANIFESTS = ['bruno.json', 'opencollection.yml', 'opencollection.yaml'];
const FOLDER_META = ['folder.bru', 'folder.yml', 'folder.yaml'];

const norm = (p) => String(p || '').replace(/\\/g, '/').replace(/^\.?\/+/, '');
const dirOf = (p) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '');
const baseOf = (p) => p.slice(p.lastIndexOf('/') + 1);
const stem = (name) => name.replace(/\.(bru|ya?ml)$/i, '');
const firstLine = (msg) => String(msg || '').split('\n')[0].slice(0, 120);

/**
 * A parse error's REASON, never a quote of the file (reviewer N3): V8's JSON.parse message
 * embeds a slice of the source, and js-yaml's message carries a code snippet after line one.
 */
function readableReason(err) {
  if (err && err.name === 'YAMLException') {
    const line = err.mark && Number.isFinite(err.mark.line) ? ` (line ${err.mark.line + 1})` : '';
    return `not valid YAML: ${String(err.reason || 'syntax error').slice(0, 80)}${line}`;
  }
  if (err instanceof SyntaxError) return 'not valid JSON';
  return firstLine(err && err.message);
}

function findRoots(paths) {
  const manifestDirs = new Set(paths.filter((p) => MANIFESTS.includes(baseOf(p).toLowerCase())).map(dirOf));
  if (manifestDirs.has('')) return [''];
  return [...manifestDirs].filter((d) => !d.includes('/')).sort();
}

function buildCollection(rootDir, files, rootName, warnings) {
  const prefix = rootDir ? `${rootDir}/` : '';
  const mine = files
    .filter((f) => f.path.startsWith(prefix))
    .map((f) => ({ rel: f.path.slice(prefix.length), text: f.text }));
  const byRel = new Map(mine.map((f) => [f.rel, f.text]));

  let manifest = {};
  if (byRel.has('bruno.json')) {
    try {
      manifest = JSON.parse(byRel.get('bruno.json')) || {};
    } catch (err) {
      warnings.push(`Could not read bruno.json (${readableReason(err)}).`);
    }
  }
  let oc = null;
  const ocText = byRel.get('opencollection.yml') ?? byRel.get('opencollection.yaml');
  if (ocText != null) {
    try {
      oc = loadImportYaml(ocText) || {};
    } catch (err) {
      warnings.push(`Could not read opencollection.yml (${readableReason(err)}).`);
    }
  }

  const name = String(manifest.name || oc?.info?.name || (rootDir ? baseOf(rootDir) : rootName) || 'Bruno Collection');
  let defaults = emptyDefaults();
  let docs = '';
  if (byRel.has('collection.bru')) {
    const d = bruDefaultsFromText(byRel.get('collection.bru'));
    defaults = d.defaults;
    docs = d.docs;
  } else if (oc) {
    defaults = ymlDefaults(oc.request);
    docs = typeof oc.docs === 'string' ? oc.docs : String(oc.docs?.content || '');
  }

  const environments = [];
  const folders = new Map(); // dir rel → folder node
  const rootNode = { children: [] };
  const folderFor = (dir) => {
    if (!dir) return rootNode;
    if (folders.has(dir)) return folders.get(dir);
    const node = { kind: 'folder', name: baseOf(dir), seq: undefined, docs: '', defaults: emptyDefaults(), children: [] };
    folders.set(dir, node);
    folderFor(dirOf(dir)).children.push(node);
    return node;
  };

  for (const { rel, text } of mine.sort((a, b) => a.rel.localeCompare(b.rel))) {
    const base = baseOf(rel);
    const lower = base.toLowerCase();
    const dir = dirOf(rel);
    if (!dir && (MANIFESTS.includes(lower) || lower === 'collection.bru')) continue;
    try {
      if (dir === 'environments' && /\.(bru|ya?ml)$/i.test(base)) {
        environments.push(
          lower.endsWith('.bru') ? bruEnvFromText(text, stem(base)) : ymlEnvToEnv(loadImportYaml(text) || {}, stem(base))
        );
        continue;
      }
      if (FOLDER_META.includes(lower)) {
        const meta = lower.endsWith('.bru') ? bruDefaultsFromText(text) : ymlFolderMeta(loadImportYaml(text) || {});
        const node = folderFor(dir);
        if (meta.name) node.name = meta.name; // the folder's own name, not its directory's
        node.seq = meta.seq;
        node.docs = meta.docs;
        node.defaults = meta.defaults;
        continue;
      }
      if (lower.endsWith('.bru')) {
        folderFor(dir).children.push(bruRequestToNode(text, stem(base)));
      } else if (/\.ya?ml$/.test(lower)) {
        const obj = loadImportYaml(text);
        if (!obj || typeof obj !== 'object') continue;
        if (String(obj.info?.type || '').toLowerCase() === 'folder') {
          const meta = ymlFolderMeta(obj);
          const node = folderFor(dir);
          if (meta.name) node.name = meta.name;
          node.seq = meta.seq;
          node.docs = meta.docs;
          node.defaults = meta.defaults;
          continue;
        }
        folderFor(dir).children.push(ymlItemToNode(obj, stem(base)));
      }
    } catch (err) {
      warnings.push(`Could not read ${rel}: ${readableReason(err)}`);
    }
  }

  const ir = { name, docs, defaults, children: rootNode.children, environments };
  return { collection: irToNormalized(ir, warnings), environments };
}

/**
 * @param {Array<{ path: string, text: string }>} files Paths relative to the picked folder.
 * @param {string} rootName The picked folder's own name.
 */
export function parseImportFolder(files, rootName = '') {
  const clean = (files || []).filter((f) => f && typeof f.text === 'string').map((f) => ({ path: norm(f.path), text: f.text }));
  const roots = findRoots(clean.map((f) => f.path));
  if (!roots.length) throw new Error(NOT_A_BRUNO_FOLDER);

  const warnings = [];
  const collections = [];
  const environments = [];
  for (const root of roots) {
    const built = buildCollection(root, clean, rootName, warnings);
    collections.push(built.collection);
    environments.push(...built.environments);
  }

  const totalRequests = collections.reduce((n, c) => n + (c.requests?.length || 0), 0);
  if (totalRequests === 0) {
    warnings.push('No HTTP requests were found; empty collections will still be created.');
  }
  const envs = environments.filter((e) => e && e.name);
  secretEnvWarning(envs, warnings);

  return {
    kind: 'collection',
    format: 'bruno_folder',
    formatLabel: formatLabel('bruno_folder'),
    collections,
    ...(envs.length ? { environments: envs } : {}),
    warnings,
  };
}
